import * as React from 'react';
import {NumberInputProvider} from './_context';
import type {NumberInputProps} from './_types';
import {NumberInputVariant} from './NumberInputVariant';

/**
 * # NumberInput
 *
 * The NumberInput allows for users to input and adjust a numeric values. It accommodates features like custom stepping, min/max constraints, wheel adjustment, and error handling.
 *
 * ## Usage
 *
 * ```jsx
 * import {NumberInput} from '@packages/systems/spring';
 * ```
 *
 * @component
 */
export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      isDisabled,
      isError,
      min,
      max,
      name,
      onBlur,
      onChange,
      onFocus,
      placeholder,
      precision,
      shouldAllowWheelAdjustment,
      shouldShowTrailingControls,
      skipMultiplier,
      step,
      prefix,
      suffix,
      value,
      variant,
      ...props
    }: NumberInputProps,
    ref
  ) => {
    return (
      <NumberInputProvider
        isDisabled={isDisabled}
        isError={isError}
        max={max}
        min={min}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        placeholder={placeholder}
        precision={precision}
        prefix={prefix}
        ref={ref}
        shouldAllowWheelAdjustment={shouldAllowWheelAdjustment}
        shouldShowTrailingControls={shouldShowTrailingControls}
        skipMultiplier={skipMultiplier}
        step={step}
        suffix={suffix}
        value={value}
        variant={variant}
      >
        <NumberInputVariant {...props} />
      </NumberInputProvider>
    );
  }
);

NumberInput.displayName = 'NumberInput';

export default NumberInput;
